import { useEffect, useMemo, useRef, useState, type HTMLInputTypeAttribute, type MutableRefObject } from 'react'
import { Pressable, Text, TextInput, View } from 'react-native'
import { useController, type Control, type FieldPath, type FieldValues, type RegisterOptions } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { isWeb } from '@/constants/app'
import { cn } from '@/utils/cn'
import { Input, type InputProps } from '@/components/base'
import { Cancel, Eye, EyeOff } from '@/components/icon'

interface FormInputProps<TValues extends FieldValues, TPath extends FieldPath<TValues>>
  extends Omit<InputProps, 'defaultValue' | 'error' | 'onChange' | 'value'> {
  label?: string
  labelRightElement?: React.ReactElement
  hint?: string
  type?: HTMLInputTypeAttribute
  control: Control<TValues>
  name: TPath
  rules?: RegisterOptions<TValues, TPath>
  hasPlaceholder?: boolean
}

export function FormInput<TValues extends FieldValues, TPath extends FieldPath<TValues>>({
  label,
  labelRightElement,
  hint,
  type,
  control,
  name,
  rules,
  className,
  rightElement: providedRightElement,
  maxLength,
  hasPlaceholder = true,
  ...props
}: FormInputProps<TValues, TPath>) {
  const { t } = useTranslation()
  const isPassword = type === 'password'
  const [isSecure, setIsSecure] = useState<boolean>(isPassword)
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [isLateFocus, setIsLateFocus] = useState(false)
  const [isClearBtnFocus, setIsClearBtnFocus] = useState(false)
  const {
    field: { ref, value, onChange },
    fieldState: { error }
  } = useController({ control, name, rules })
  const hasError = error !== undefined
  const inputRef: MutableRefObject<TextInput | null> = useRef(null)
  const hasClearButton = isWeb ? (isClearBtnFocus || isLateFocus) && value : isFocus && value

  const generatedRightElement = useMemo(() => {
    const Clear = hasClearButton ? (
      <Pressable
        accessibilityLabel={t('ui:clearInput')}
        accessibilityRole={'button'}
        className={cn('size-8 items-center justify-center outline-offset-1', hasError && 'outline-danger')}
        hitSlop={6}
        onPress={() => {
          onChange('')
          setIsClearBtnFocus(false)
          inputRef.current?.focus()
        }}
        onFocus={() => setIsClearBtnFocus(true)}
        onBlur={() => setIsClearBtnFocus(false)}
      >
        <Cancel className={'size-5 text-text-quaternary'} />
      </Pressable>
    ) : undefined

    const Secure = isSecure ? (
      <Pressable
        accessibilityLabel={t('ui:showPassword')}
        accessibilityRole={'button'}
        className={cn('size-8 items-center justify-center outline-offset-1', hasError && 'outline-danger')}
        hitSlop={6}
        onPress={() => {
          setIsSecure((state) => !state)
          inputRef.current?.focus()
        }}
      >
        <EyeOff className={'size-5 text-text-quaternary'} />
      </Pressable>
    ) : (
      <Pressable
        accessibilityLabel={t('ui:hidePassword')}
        accessibilityRole={'button'}
        className={cn('size-8 items-center justify-center outline-offset-1', hasError && 'outline-danger')}
        hitSlop={6}
        onPress={() => {
          setIsSecure((state) => !state)
          inputRef.current?.focus()
        }}
      >
        <Eye className={'size-5 text-text-quaternary'} />
      </Pressable>
    )

    if (isPassword) {
      return (
        <>
          {Clear}
          {Secure}
        </>
      )
    }
    return Clear
  }, [hasClearButton, isPassword, isSecure, setIsSecure, onChange, hasError, t])

  useEffect(() => {
    setIsLateFocus(isFocus)
  }, [isFocus])

  return (
    <View className={'gap-y-1'}>
      <View className={'flex-row items-baseline justify-between'}>
        {label && <Text className={'text-sm font-medium text-text-secondary'}>{label}</Text>}
        {labelRightElement}
      </View>
      <Input
        {...props}
        testID={props.testID ?? `input-${String(name).replaceAll('.', '-')}`}
        accessibilityLabel={props.accessibilityLabel ?? label ?? props.placeholder}
        ref={(e) => {
          ref(e)
          inputRef.current = e
        }}
        value={value}
        onChange={onChange}
        onFocus={() => setIsFocus(true)}
        onBlur={() => setIsFocus(false)}
        secureTextEntry={isSecure}
        rightElement={providedRightElement ?? generatedRightElement}
        error={hasError}
        maxLength={isPassword ? (maxLength ?? 64) : maxLength}
        className={className}
      />
      {!hasError && hint && <Text className={'text-xs font-medium text-text-tertiary'}>{hint}</Text>}
      {hasError && <Text className={'text-xs font-medium text-text-danger'}>{error.message}</Text>}
      {hasPlaceholder && !hasError && !hint && <View className={'h-4'} />}
    </View>
  )
}
